import {defineStore} from 'pinia'
import {ref, shallowReactive, shallowRef, toRaw} from 'vue'
import {i18n} from '@toolify/client/src/setup/i18n'
import {ILocalMessage} from '@toolify/client/src/modules/notifications/types/ILocalMessage'
import {IMessageInput} from '@toolify/client/src/modules/notifications/types/IMessageInput'
import {IMessageProgress} from '@toolify/client/src/modules/notifications/types/IMessageProgress'
import {IMessageCallbackContext} from '@toolify/client/src/modules/notifications/types/IMessageCallbackContext'
import {MessageSeverityType} from '@toolify/client/src/modules/notifications/enum/MessageSeverityType'
import {MessageStateType} from '@toolify/client/src/modules/notifications/enum/MessageStateType'
import {MessageActionKindType} from '@toolify/client/src/modules/notifications/enum/MessageActionKindType'
import {MessageDismissReasonType} from '@toolify/client/src/modules/notifications/enum/MessageDismissReasonType'
import {
  MessageAnnouncementChannelType,
} from '@toolify/client/src/modules/notifications/enum/MessageAnnouncementChannelType'
import {omitUndefinedFields} from '@toolify/client/src/modules/notifications/functions/omitUndefinedFields'
import {LocalMessagePauseReasonType} from './enum/LocalMessagePauseReasonType'
import {LocalMessageUpdateModeType} from './enum/LocalMessageUpdateModeType'
import {LocalMessageStoreEventType} from './enum/LocalMessageStoreEventType'
import {LocalMessageDescribeModeType} from './enum/LocalMessageDescribeModeType'
import {ILocalMessageAnnouncement} from './types/ILocalMessageAnnouncement'
import {ILocalMessageInteraction} from './types/ILocalMessageInteraction'
import {ILocalMessageStoreEvent} from './types/ILocalMessageStoreEvent'
import {ILocalMessageTimerState} from './types/ILocalMessageTimerState'

export const LOCAL_MESSAGE_DEFAULT_DURATION_MS = 4000
// WCAG 2.2.1: a keyboard user must have time to reach the action (Tab / F8)
export const LOCAL_MESSAGE_KEYBOARD_MIN_DURATION_MS = 10000
// fallback removal when the card's own transitionend never comes (reduced motion, collapsed card)
export const LOCAL_MESSAGE_LEAVE_MS = 320
export const LOCAL_MESSAGE_TICK_MS = 100
export const LOCAL_MESSAGE_ANNOUNCEMENT_THROTTLE_MS = 900
const MAX_ACTIONS = 2
// announcements kept while no overlay is mounted; a longer backlog is not worth reading out
const MAX_PENDING_ANNOUNCEMENTS = 10
const PROGRESS_MILESTONE_RATIO = 0.5

interface ILocalMessageRuntime extends ILocalMessageInteraction {
  remaining: number
  hasAnnouncedProgressMilestone: boolean
  hasExplicitShowAge: boolean
  hasExplicitPinned: boolean
  collapseAnnouncementTimeout: ReturnType<typeof setTimeout> | null
  leaveTimeout: ReturnType<typeof setTimeout> | null
  leaveReason: MessageDismissReasonType | null
}

type LocalMessageEventListener = (event: ILocalMessageStoreEvent) => void

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

function normalizeProgress(progress: IMessageProgress): IMessageProgress {
  const total = Number(progress.total) > 0 ? Number(progress.total) : 100
  return {
    ...progress,
    total,
    value: clamp(Number(progress.value) || 0, 0, total),
  }
}

function trimSentenceEnd(text: string): string {
  return text.replace(/[\s.…]+$/u, '')
}

// Joins announcement parts with '. ' without doubling the punctuation the parts already end with
function joinSentences(parts: (string | null | undefined)[]): string {
  const filledParts = parts.filter((part): part is string => !!part && !!part.trim())
  return filledParts.map((part, index) => {
    return index < filledParts.length - 1 ? trimSentenceEnd(part) : part
  }).join('. ')
}

function normalizeMessage(input: Partial<ILocalMessage> & {id: string}, isKeyboardModality: boolean): ILocalMessage {
  const severity = input.severity ?? MessageSeverityType.Neutral
  const isProgress = severity === MessageSeverityType.Progress
  const actions = (input.actions ?? []).slice(0, MAX_ACTIONS).map(action => {
    return {
      ...omitUndefinedFields(action),
      kind: action.kind ?? MessageActionKindType.Secondary,
      label: action.label ?? '',
    }
  })
  let duration = isProgress ? 0 : Math.max(0, Number(input.duration ?? LOCAL_MESSAGE_DEFAULT_DURATION_MS) || 0)
  // a persistent card (progress included) is pinned, so it never folds into the "+N" pill with a stopped timer
  const isPersistent = duration === 0
  if(isKeyboardModality && duration > 0 && actions.length) {
    duration = Math.max(duration, LOCAL_MESSAGE_KEYBOARD_MIN_DURATION_MS)
  }

  return {
    id: input.id,
    severity,
    title: input.title ?? '',
    text: input.text ?? '',
    actions,
    progress: input.progress ? normalizeProgress(input.progress) : null,
    duration,
    dismissible: input.dismissible ?? !isProgress,
    errorId: input.errorId ?? null,
    collapseKey: input.collapseKey ?? null,
    replaceKey: input.replaceKey ?? null,
    pinned: input.pinned ?? isPersistent,
    timerBar: input.timerBar ?? false,
    clampLines: Math.max(0, Number(input.clampLines) || 0),
    showAge: input.showAge ?? (duration === 0 && !isProgress),
    count: input.count ?? 1,
    createdAt: input.createdAt ?? Date.now(),
    seqNo: input.seqNo ?? 0,
    state: input.state ?? MessageStateType.Enter,
    collapsed: input.collapsed ?? false,
    onUndo: input.onUndo,
    onDismiss: input.onDismiss,
  }
}

function compareNewestFirst(first: ILocalMessage, second: ILocalMessage): number {
  return second.createdAt - first.createdAt || second.seqNo - first.seqNo
}

// Local (client-side) messages: the model behind the cards in FloatingNotificationsOverlay.
// Holds no DOM; the card writes --msg-timer itself on every tick, so a tick never re-renders anything.
// The list is replaced on every add/remove and each message is shallowly reactive: nested values
// (progress, actions, callbacks) are always swapped whole, never mutated.
export const useLocalMessageStore = defineStore('localMessage', () => {
  const messages = shallowRef<ILocalMessage[]>([])
  const lastAnnouncement = ref<ILocalMessageAnnouncement | null>(null)
  // grows wherever the stack order may change, the overlay re-runs its layout on it
  const layoutRevision = ref(0)

  const runtimeById = new Map<string, ILocalMessageRuntime>()
  const environmentPauseReasons = new Set<LocalMessagePauseReasonType>([LocalMessagePauseReasonType.RendererDetached])
  const listeners = new Set<LocalMessageEventListener>()
  let idSequence = 0
  let orderSequence = 0
  let announcementSequence = 0
  let isKeyboardModality = false
  let hasAnnouncedShortcut = false
  let pendingAnnouncements: Omit<ILocalMessageAnnouncement, 'seq'>[] = []
  let tickInterval: ReturnType<typeof setInterval> | null = null
  let lastTickAt = 0
  let lastOverflowCount = 0
  let overflowAnnouncementTimeout: ReturnType<typeof setTimeout> | null = null

  function t(key: string, named?: Record<string, unknown>, plural?: number): string {
    if(plural !== undefined) {
      return i18n.global.t(key, named ?? {}, plural)
    }
    return named ? i18n.global.t(key, named) : i18n.global.t(key)
  }

  function emit(event: ILocalMessageStoreEvent) {
    listeners.forEach(listener => listener(event))
  }

  function subscribeToEvents(listener: LocalMessageEventListener): () => void {
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  }

  // typed access for components: Pinia's unwrapped state type mangles RouteLocationRaw in actions[].to
  function getMessages(): ILocalMessage[] {
    return messages.value
  }

  function findMessage(id: string): ILocalMessage | undefined {
    return messages.value.find(message => message.id === id)
  }

  function findLiveMessage(predicate: (message: ILocalMessage) => boolean): ILocalMessage | undefined {
    return messages.value.find(message => message.state !== MessageStateType.Leave && predicate(message))
  }

  function getLiveMessage(id: string): ILocalMessage | undefined {
    return findLiveMessage(message => message.id === id)
  }

  function createRuntime(duration: number, hasExplicitShowAge = false, hasExplicitPinned = false): ILocalMessageRuntime {
    return {
      remaining: duration,
      hovered: false,
      focused: false,
      touched: false,
      hasAnnouncedProgressMilestone: false,
      hasExplicitShowAge,
      hasExplicitPinned,
      collapseAnnouncementTimeout: null,
      leaveTimeout: null,
      leaveReason: null,
    }
  }

  function getRuntime(message: ILocalMessage): ILocalMessageRuntime {
    let runtime = runtimeById.get(message.id)
    if(!runtime) {
      runtime = createRuntime(message.duration)
      runtimeById.set(message.id, runtime)
    }
    return runtime
  }

  function disposeRuntime(id: string) {
    const runtime = runtimeById.get(id)
    if(!runtime) {
      return
    }
    if(runtime.collapseAnnouncementTimeout) {
      clearTimeout(runtime.collapseAnnouncementTimeout)
    }
    if(runtime.leaveTimeout) {
      clearTimeout(runtime.leaveTimeout)
    }
    runtimeById.delete(id)
  }

  // --- timers ---------------------------------------------------------------

  function isPaused(message: ILocalMessage, runtime: ILocalMessageRuntime): boolean {
    return message.collapsed || runtime.hovered || runtime.focused || runtime.touched
  }

  function hasTimedLiveMessage(): boolean {
    return messages.value.some(message => message.state !== MessageStateType.Leave && message.duration > 0)
  }

  // no renderer, no countdown: a push without a mounted stack must not leave a 100 ms interval behind
  function syncTicker() {
    const shouldRun = hasTimedLiveMessage() && !environmentPauseReasons.has(LocalMessagePauseReasonType.RendererDetached)
    if(shouldRun && !tickInterval) {
      lastTickAt = Date.now()
      tickInterval = setInterval(tick, LOCAL_MESSAGE_TICK_MS)
      return
    }
    if(!shouldRun && tickInterval) {
      clearInterval(tickInterval)
      tickInterval = null
    }
  }

  function tick() {
    const now = Date.now()
    const elapsed = environmentPauseReasons.size ? 0 : now - lastTickAt
    lastTickAt = now
    const expiredIds: string[] = []
    for(const message of messages.value.map(item => toRaw(item))) {
      if(message.state === MessageStateType.Leave || !message.duration) {
        continue
      }
      const runtime = runtimeById.get(message.id)
      if(!runtime) {
        continue
      }
      if(elapsed > 0 && !isPaused(message, runtime)) {
        runtime.remaining -= elapsed
      }
      if(runtime.remaining <= 0) {
        expiredIds.push(message.id)
      }
    }
    expiredIds.forEach(id => dismiss(id, MessageDismissReasonType.Timeout))
    emit({type: LocalMessageStoreEventType.Tick})
    syncTicker()
  }

  function getTimerState(id: string): ILocalMessageTimerState | null {
    const message = messages.value.find(item => item.id === id)
    const runtime = runtimeById.get(id)
    if(!message || !runtime || !message.duration) {
      return null
    }
    return {
      ratio: clamp(runtime.remaining / message.duration, 0, 1),
      remainingMs: Math.max(0, Math.round(runtime.remaining)),
    }
  }

  function setPausedByEnvironment(reason: LocalMessagePauseReasonType, isPausedByReason: boolean) {
    if(isPausedByReason) {
      environmentPauseReasons.add(reason)
    } else {
      environmentPauseReasons.delete(reason)
    }
    // time spent paused (throttled background intervals included) must never count down a card
    lastTickAt = Date.now()
    if(reason === LocalMessagePauseReasonType.RendererDetached) {
      syncTicker()
      if(!isPausedByReason) {
        flushPendingAnnouncements()
      }
    }
  }

  function setInteraction(id: string, interaction: Partial<ILocalMessageInteraction>) {
    const runtime = runtimeById.get(id)
    if(!runtime) {
      return
    }
    Object.assign(runtime, omitUndefinedFields(interaction))
  }

  function getInteraction(id: string): ILocalMessageInteraction {
    const runtime = runtimeById.get(id)
    return {
      hovered: !!runtime?.hovered,
      focused: !!runtime?.focused,
      touched: !!runtime?.touched,
    }
  }

  function setKeyboardModality(isKeyboard: boolean) {
    isKeyboardModality = isKeyboard
  }

  // --- announcements ------------------------------------------------------

  // Before the overlay mounts (splash screen) announcements wait, then go out as one per channel
  function announce(channel: MessageAnnouncementChannelType, text: string) {
    if(!text) {
      return
    }
    if(environmentPauseReasons.has(LocalMessagePauseReasonType.RendererDetached)) {
      pendingAnnouncements = [...pendingAnnouncements, {
        channel,
        text,
      }].slice(-MAX_PENDING_ANNOUNCEMENTS)
      return
    }
    lastAnnouncement.value = {
      channel,
      text,
      seq: ++announcementSequence,
    }
  }

  function flushPendingAnnouncements() {
    const pending = pendingAnnouncements
    pendingAnnouncements = []
    const channels = [MessageAnnouncementChannelType.Assertive, MessageAnnouncementChannelType.Polite]
    channels.forEach(channel => {
      announce(channel, joinSentences(pending.filter(item => item.channel === channel).map(item => item.text)))
    })
  }

  function getChannel(message: ILocalMessage): MessageAnnouncementChannelType {
    return message.severity === MessageSeverityType.Error
      ? MessageAnnouncementChannelType.Assertive
      : MessageAnnouncementChannelType.Polite
  }

  function describeMessage(message: ILocalMessage, mode: LocalMessageDescribeModeType): string {
    let text = joinSentences([message.title, message.text])
    if(message.count > 1) {
      text = `${trimSentenceEnd(text)}, ${t('notifications.messages.timesCount', {count: message.count}, message.count)}`
    }
    if(mode === LocalMessageDescribeModeType.Update) {
      text = `${trimSentenceEnd(text)} ${t('notifications.messages.updatedSuffix')}`
    }
    if(message.actions.length) {
      const actionLabels = message.actions.map(action => action.label).filter(Boolean).join(', ')
      text = joinSentences([text, t('notifications.messages.availableActions', {actions: actionLabels})])
      if(!hasAnnouncedShortcut && mode !== LocalMessageDescribeModeType.Surface) {
        hasAnnouncedShortcut = true
        text = joinSentences([text, t('notifications.messages.shortcutHint')])
      }
    }
    return text
  }

  function announceMessage(message: ILocalMessage, mode: LocalMessageDescribeModeType) {
    if(mode !== LocalMessageDescribeModeType.Collapse) {
      announce(getChannel(message), describeMessage(message, mode))
      return
    }
    // a burst of ×N hits is announced once, after the last one ("Copied, 15 times")
    const runtime = getRuntime(message)
    if(runtime.collapseAnnouncementTimeout) {
      clearTimeout(runtime.collapseAnnouncementTimeout)
    }
    runtime.collapseAnnouncementTimeout = setTimeout(() => {
      runtime.collapseAnnouncementTimeout = null
      const current = findMessage(message.id)
      if(current) {
        announce(getChannel(current), describeMessage(current, LocalMessageDescribeModeType.Collapse))
      }
    }, LOCAL_MESSAGE_ANNOUNCEMENT_THROTTLE_MS)
  }

  // --- operations -----------------------------------------------------------

  // A push that takes over a live card: the old input's onDismiss runs once, with 'replaced',
  // whenever the takeover drops it (always for replaceKey, for a known id only when a new one comes)
  function takeOver(existing: ILocalMessage, patch: IMessageInput, mode: LocalMessageUpdateModeType): string {
    const previous = {...toRaw(existing)}
    const id = update(existing.id, patch, mode) ?? existing.id
    if(mode === LocalMessageUpdateModeType.Replace || existing.onDismiss !== previous.onDismiss) {
      previous.onDismiss?.(MessageDismissReasonType.Replaced, previous)
    }
    return id
  }

  function push(input: IMessageInput): string {
    const cleanInput = omitUndefinedFields(input)
    if(cleanInput.id) {
      const existing = findMessage(cleanInput.id)
      if(existing?.state === MessageStateType.Leave) {
        finishLeave(existing.id)
      } else if(existing) {
        return takeOver(existing, cleanInput, LocalMessageUpdateModeType.Merge)
      }
    }

    const seqNo = ++orderSequence
    if(cleanInput.replaceKey) {
      const existing = findLiveMessage(message => message.replaceKey === cleanInput.replaceKey)
      if(existing) {
        return takeOver(existing, {
          ...cleanInput,
          id: existing.id,
          createdAt: Date.now(),
        }, LocalMessageUpdateModeType.Replace)
      }
    }

    if(cleanInput.collapseKey) {
      const existing = findLiveMessage(message => message.collapseKey === cleanInput.collapseKey)
      if(existing) {
        bump(existing, seqNo)
        // the folded input never becomes a card, so its onDismiss runs now
        cleanInput.onDismiss?.(MessageDismissReasonType.Collapsed, existing)
        return existing.id
      }
    }

    const message = shallowReactive(normalizeMessage({
      ...cleanInput,
      id: cleanInput.id || `m${++idSequence}`,
      seqNo,
    }, isKeyboardModality))
    messages.value = [...messages.value, message]
    runtimeById.set(message.id, createRuntime(
      message.duration,
      cleanInput.showAge !== undefined,
      cleanInput.pinned !== undefined,
    ))
    syncTicker()
    layoutRevision.value++
    announceMessage(message, LocalMessageDescribeModeType.Push)
    return message.id
  }

  // Collapse drops the new text on purpose: a different text needs a different collapseKey
  function bump(message: ILocalMessage, seqNo: number) {
    message.count += 1
    message.createdAt = Date.now()
    message.seqNo = seqNo
    getRuntime(message).remaining = message.duration
    emit({
      type: LocalMessageStoreEventType.Bump,
      id: message.id,
    })
    layoutRevision.value++
    announceMessage(message, LocalMessageDescribeModeType.Collapse)
  }

  function update(
    id: string,
    patch: Partial<IMessageInput>,
    mode: LocalMessageUpdateModeType = LocalMessageUpdateModeType.Merge,
  ): string | null {
    const message = getLiveMessage(id)
    if(!message) {
      return null
    }
    const runtime = getRuntime(message)
    const cleanPatch = omitUndefinedFields(patch)
    const current = {...toRaw(message)}
    let base: Partial<ILocalMessage>
    if(mode === LocalMessageUpdateModeType.Replace) {
      base = {
        collapseKey: current.collapseKey,
        replaceKey: current.replaceKey,
      }
    } else {
      base = current
      if(cleanPatch.showAge === undefined && !runtime.hasExplicitShowAge) {
        delete base.showAge
      }
      // a default pin follows the new duration
      if(cleanPatch.pinned === undefined && !runtime.hasExplicitPinned) {
        delete base.pinned
      }
      // leaving the progress severity without saying what happens to the bar would keep it (and hide ×)
      const isLeavingProgress = current.severity === MessageSeverityType.Progress
        && !!cleanPatch.severity
        && cleanPatch.severity !== MessageSeverityType.Progress
      if(isLeavingProgress && !('progress' in cleanPatch)) {
        base.progress = null
      }
      if(isLeavingProgress && cleanPatch.dismissible === undefined) {
        delete base.dismissible
      }
    }

    const next = normalizeMessage({
      ...base,
      ...cleanPatch,
      id: message.id,
      createdAt: cleanPatch.createdAt || current.createdAt,
      seqNo: current.seqNo,
      state: current.state === MessageStateType.Enter ? MessageStateType.Enter : MessageStateType.Visible,
      collapsed: current.collapsed,
    }, isKeyboardModality)
    Object.assign(message, next)

    runtime.remaining = message.duration
    if(mode === LocalMessageUpdateModeType.Replace) {
      runtime.hasAnnouncedProgressMilestone = false
      runtime.hasExplicitShowAge = cleanPatch.showAge !== undefined
      runtime.hasExplicitPinned = cleanPatch.pinned !== undefined
    } else {
      runtime.hasExplicitShowAge = runtime.hasExplicitShowAge || cleanPatch.showAge !== undefined
      runtime.hasExplicitPinned = runtime.hasExplicitPinned || cleanPatch.pinned !== undefined
    }

    emit({
      type: LocalMessageStoreEventType.Morph,
      id: message.id,
    })
    syncTicker()
    layoutRevision.value++
    announceMessage(message, LocalMessageDescribeModeType.Update)
    return message.id
  }

  // Updates the bar in place: no timer reset, only the 50 % milestone is announced
  function setProgress(id: string, value: number, label?: string, text?: string): string | null {
    const message = getLiveMessage(id)
    if(!message?.progress) {
      return null
    }
    const total = message.progress.total || 100
    const clampedValue = clamp(Number(value) || 0, 0, total)
    message.progress = {
      ...message.progress,
      value: clampedValue,
      label: label ?? message.progress.label,
      indeterminate: false,
    }
    if(text != null) {
      message.text = text
    }

    const runtime = getRuntime(message)
    const ratio = clampedValue / total
    if(ratio < PROGRESS_MILESTONE_RATIO) {
      runtime.hasAnnouncedProgressMilestone = false
    } else if(!runtime.hasAnnouncedProgressMilestone && ratio < 1) {
      runtime.hasAnnouncedProgressMilestone = true
      announce(
        MessageAnnouncementChannelType.Polite,
        joinSentences([message.title, message.progress.label || `${Math.round(ratio * 100)}%`]),
      )
    }
    return message.id
  }

  // The card stays in the list with state 'leave' (fade out, aria-hidden) until finishLeave()
  function dismiss(id: string, reason: MessageDismissReasonType = MessageDismissReasonType.Close): boolean {
    const message = getLiveMessage(id)
    if(!message) {
      return false
    }
    const runtime = getRuntime(message)
    message.state = MessageStateType.Leave
    runtime.leaveReason = reason
    runtime.leaveTimeout = setTimeout(() => {
      finishLeave(id)
    }, LOCAL_MESSAGE_LEAVE_MS)
    syncTicker()
    return true
  }

  // Called by the card on its own transitionend, or by the fallback timeout
  function finishLeave(id: string) {
    const index = messages.value.findIndex(message => message.id === id && message.state === MessageStateType.Leave)
    if(index < 0) {
      return
    }
    const message = messages.value[index]
    const reason = runtimeById.get(id)?.leaveReason ?? MessageDismissReasonType.Close
    disposeRuntime(id)
    messages.value = messages.value.filter((_, messageIndex) => messageIndex !== index)
    layoutRevision.value++
    syncTicker()
    message.onDismiss?.(reason, message)
  }

  // Removes every card at once, without the leave animation
  function clear() {
    const removedMessages = messages.value.map(message => {
      return {
        message,
        reason: runtimeById.get(message.id)?.leaveReason ?? MessageDismissReasonType.Clear,
      }
    })
    Array.from(runtimeById.keys()).forEach(disposeRuntime)
    messages.value = []
    pendingAnnouncements = []
    if(overflowAnnouncementTimeout) {
      clearTimeout(overflowAnnouncementTimeout)
      overflowAnnouncementTimeout = null
    }
    lastOverflowCount = 0
    layoutRevision.value++
    syncTicker()
    removedMessages.forEach(({message, reason}) => {
      message.onDismiss?.(reason, message)
    })
  }

  function markVisible(id: string) {
    const message = findMessage(id)
    if(message?.state === MessageStateType.Enter) {
      message.state = MessageStateType.Visible
    }
  }

  function createCallbackContext(message: ILocalMessage): IMessageCallbackContext {
    const id = message.id
    return {
      id,
      message,
      update: patch => update(id, patch),
      dismiss: (reason = MessageDismissReasonType.Action) => dismiss(id, reason),
      setProgress: (value, label, text) => setProgress(id, value, label, text),
    }
  }

  function runAction(id: string, actionIndex: number) {
    const message = getLiveMessage(id)
    const action = message?.actions[actionIndex]
    if(!message || !action) {
      return
    }
    const context = createCallbackContext(message)
    action.onClick?.(context)
    if(action.kind === MessageActionKindType.Undo) {
      message.onUndo?.(context)
    }
    if(action.closes !== false && getLiveMessage(id)) {
      dismiss(id, MessageDismissReasonType.Action)
    }
  }

  // "Close all" in the overflow pill: every flow card that can be closed, visible or collapsed
  function dismissAllInFlow() {
    messages.value
      .filter(message => message.state !== MessageStateType.Leave && !message.pinned && message.dismissible)
      .forEach(message => dismiss(message.id, MessageDismissReasonType.CloseAll))
  }

  // Applies the overlay's layout: collapsed cards pause, surfaced ones and the hidden count are announced
  function applyLayout(collapsedIds: string[], overflowCount: number) {
    const collapsedIdSet = new Set(collapsedIds)
    const surfacedMessages: ILocalMessage[] = []
    messages.value.forEach(message => {
      if(message.state === MessageStateType.Leave) {
        return
      }
      const isCollapsed = collapsedIdSet.has(message.id)
      if(message.collapsed && !isCollapsed) {
        surfacedMessages.push(message)
      }
      if(message.collapsed !== isCollapsed) {
        message.collapsed = isCollapsed
      }
    })

    if(overflowCount !== lastOverflowCount) {
      lastOverflowCount = overflowCount
      if(overflowAnnouncementTimeout) {
        clearTimeout(overflowAnnouncementTimeout)
      }
      overflowAnnouncementTimeout = setTimeout(() => {
        overflowAnnouncementTimeout = null
        if(overflowCount > 0) {
          announce(MessageAnnouncementChannelType.Polite, t('notifications.messages.hiddenAnnouncement', {count: overflowCount}))
        }
      }, LOCAL_MESSAGE_ANNOUNCEMENT_THROTTLE_MS)
    }

    if(surfacedMessages.length) {
      announce(
        MessageAnnouncementChannelType.Polite,
        joinSentences(surfacedMessages.map(message => describeMessage(message, LocalMessageDescribeModeType.Surface))),
      )
    }
  }

  // Target of F8 and of the focus hand-over when a focused card closes
  function getNewestVisibleMessageId(excludedId?: string): string | null {
    const candidates = messages.value.filter(message => {
      return message.state !== MessageStateType.Leave && !message.collapsed && message.id !== excludedId
    })
    return [...candidates].sort(compareNewestFirst)[0]?.id ?? null
  }

  return {
    messages,
    lastAnnouncement,
    layoutRevision,
    push,
    update,
    setProgress,
    dismiss,
    clear,
    getMessages,
    finishLeave,
    markVisible,
    runAction,
    dismissAllInFlow,
    applyLayout,
    getNewestVisibleMessageId,
    getTimerState,
    setInteraction,
    getInteraction,
    setPausedByEnvironment,
    setKeyboardModality,
    announce,
    subscribeToEvents,
  }
})
